import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Badge, BootstrapModalConfirm } from "components/bootstrap";
import usePermissions from "hooks/usePermissions";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useToggleWorkflowStatus from "./hooks/useToggleWorkflowStatus";
const StatusBadge = styled(Badge)`
  opacity: ${({ $isUpdating }) => $isUpdating ? 0.7 : 1};
`;
const STATUS_LABEL = {
  running: "workflow_status.status_enabled",
  paused: "workflow_status.status_disabled"
};
const WorkflowStatus = ({ workflow, onSuccess }) => {
  const { t } = useTranslation("enterprise_ai");
  const sendTelemetry = useSendTelemetry();
  const { isPermitted } = usePermissions();
  const { mutateAsync: toggleStatus, isPending } = useToggleWorkflowStatus();
  const [showConfirmDisable, setShowConfirmDisable] = useState(false);
  const isRunning = !workflow.disabled;
  const hasConfig = (workflow == null ? void 0 : workflow.model) && (workflow == null ? void 0 : workflow.provider);
  const canToggle = isPermitted("agent_workflow:manage");
  const title = useMemo(() => {
    if (!hasConfig) return t("workflow_status.no_model_provider");
    if (!canToggle) return isRunning ? t("workflow_status.enabled") : t("workflow_status.disabled");
    if (isRunning) return t("workflow_status.disable_workflow");
    return t("workflow_status.enable_workflow");
  }, [canToggle, hasConfig, isRunning, t]);
  const toggle = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AI_AGENT_WORKFLOW.WORKFLOW_STATUS_TOGGLED, {
      app_section: "aiagent-workflows",
      event_details: { workflow_type: workflow.id, next_status: isRunning ? "paused" : "running" }
    });
    return toggleStatus(workflow).then(onSuccess);
  };
  const onClick = (e) => {
    e.stopPropagation();
    if (isPending) return;
    if (isRunning) {
      setShowConfirmDisable(true);
    } else {
      toggle();
    }
  };
  const onConfirmDisable = () => toggle().then(() => setShowConfirmDisable(false));
  const label = isPending ? t("workflow_status.updating") : title;
  const toggleIcon = isRunning ? "pause" : "play_arrow";
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    StatusBadge,
    {
      color: isRunning ? "success" : "warning",
      variant: "light",
      onClick: canToggle ? onClick : void 0,
      title: label,
      "aria-label": label,
      rightIcon: canToggle ? toggleIcon : void 0,
      $isUpdating: isPending
    },
    t(isRunning ? STATUS_LABEL.running : STATUS_LABEL.paused)
  ), showConfirmDisable && /* @__PURE__ */ React.createElement(
    BootstrapModalConfirm,
    {
      showModal: true,
      title: t("workflow_status.disable_workflow"),
      onConfirm: onConfirmDisable,
      onCancel: () => setShowConfirmDisable(false)
    },
    t("workflow_status.confirm_disable", { title: workflow.title })
  ));
};
export default WorkflowStatus;
